import React, { useState } from 'react';
import {
  Stack,
  Button,
  Group,
  Text,
  Box,
  Badge,
  Alert,
  Loader,
  Progress,
  Table,
  Accordion,
  Code,
  ScrollArea,
  Collapse,
  NumberInput,
  useMantineTheme,
  JsonInput,
  Divider,
  Card,
} from '@mantine/core';
import {
  IconCheck,
  IconTrash,
  IconRefresh,
  IconAlertCircle,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconInfoCircle,
} from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import { useAuth0 } from '@auth0/auth0-react';
import { validateProcessedField } from '../api/integrations2.0';

interface ValidationSample {
  input: Record<string, any>;
  output: any;
  error?: string;
}

interface ValidationResult {
  fieldName: string;
  samples: ValidationSample[];
  successCount: number;
  errorCount: number;
}

interface ProcessedFieldSettingsBoxProps {
  field: any;
  updateField: (args: any) => Promise<any>;
  integrationKey: string;
  object: string;
  originObject: string;
  onDeleteField?: (field: any) => void;
  isDeleting?: boolean;
}

export const ProcessedFieldSettingsBox: React.FC<ProcessedFieldSettingsBoxProps> = ({
  field,
  updateField,
  integrationKey,
  object,
  originObject,
  onDeleteField,
  isDeleting = false,
}) => {
  const auth0 = useAuth0();
  const theme = useMantineTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [sampleCount, setSampleCount] = useState(3);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [expandedSamples, setExpandedSamples] = useState<Set<number>>(new Set());
  const [jsonValue, setJsonValue] = useState(() => {
    try {
      return JSON.stringify(field, null, 2);
    } catch {
      return '{}';
    }
  });
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleRunValidation = async () => {
    setIsValidating(true);
    setValidationError(null);
    setValidationResult(null);

    try {
      const result = await validateProcessedField(
        integrationKey,
        object,
        originObject,
        field.key,
        sampleCount || 3,
        auth0,
      );
      setValidationResult(result);
    } catch (err: any) {
      setValidationError(err.message || 'Failed to validate field');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    // Validate JSON
    let parsedField;
    try {
      parsedField = JSON.parse(jsonValue);
    } catch (error) {
      setJsonError('Invalid JSON format');
      showNotification({
        title: 'Invalid JSON',
        message: 'Please fix the JSON syntax errors',
        color: 'red',
      });
      return;
    }

    setJsonError(null);
    setIsSaving(true);
    try {
      await updateField({
        fieldName: parsedField.key || field.key,
        field: parsedField,
      });
      showNotification({
        title: 'Field updated',
        message: 'Processed field updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error saving processed field:', error);
      showNotification({
        title: 'Error updating field',
        message: 'Failed to update processed field',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset JSON to original field
    try {
      setJsonValue(JSON.stringify(field, null, 2));
      setJsonError(null);
    } catch {
      setJsonValue('{}');
    }
  };

  const handleDeleteField = () => {
    if (onDeleteField) {
      onDeleteField(field);
    }
  };

  const toggleSampleExpansion = (index: number) => {
    const newExpanded = new Set(expandedSamples);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSamples(newExpanded);
  };

  const getSuccessRate = () => {
    if (!validationResult) return 0;
    const total = validationResult.successCount + validationResult.errorCount;
    if (total === 0) return 0;
    return (validationResult.successCount / total) * 100;
  };

  return (
    <Stack spacing="sm">
      <Text size="sm" weight={500}>
        Processed Field Configuration
      </Text>

      {/* Required Fields Info */}
      <Box>
        <Group
          spacing="xs"
          align="center"
          onClick={() => setShowInfo(!showInfo)}
          sx={(theme) => ({
            cursor: 'pointer',
            backgroundColor: showInfo ? theme.colors.blue[0] : 'transparent',
            padding: '4px 8px',
            borderRadius: theme.radius.sm,
            '&:hover': {
              backgroundColor: theme.colors.blue[0],
            },
          })}
        >
          <IconInfoCircle size={16} color={theme.colors.blue[6]} />
          <Text size="xs" color="dimmed" weight={500}>
            Required fields for processed fields
          </Text>
          {showInfo ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </Group>
        <Collapse in={showInfo}>
          <Box mt="xs">
            <Code block style={{ fontSize: 11 }}>
              {`{
  "key": "fieldName",
  "name": "Field Display Name",
  "fieldType": "processed",
  "type": "text|number|date|...",
  "origin": "integrationKey",
  "pipeline": {
    "operation": "...",
    // ... pipeline configuration
  }
}`}
            </Code>
          </Box>
        </Collapse>
      </Box>

      {/* JSON Editor */}
      <Box>
        <JsonInput
          //   label="Pipeline Configuration (JSON)"
          placeholder="Enter JSON pipeline"
          value={jsonValue}
          onChange={setJsonValue}
          formatOnBlur
          validationError={jsonError || 'Invalid JSON'}
          autosize
          minRows={10}
          maxRows={20}
          error={jsonError}
        />
      </Box>

      {/* Cancel / Save / Verify Buttons */}
      <Group position="apart">
        <Button
          variant="subtle"
          onClick={() => setShowValidation(!showValidation)}
          leftIcon={<IconRefresh size={14} />}
        >
          Verify
        </Button>
        <Group>
          {onDeleteField && (
            <Button
              variant="outline"
              color="red"
              size="sm"
              leftIcon={<IconTrash size={14} />}
              onClick={handleDeleteField}
              disabled={isDeleting}
              loading={isDeleting}
            >
              Delete Field
            </Button>
          )}
          <Button onClick={handleSave} loading={isSaving} leftIcon={<IconCheck size={14} />}>
            Save Changes
          </Button>
        </Group>
      </Group>

      {/* Validation Collapsible Section */}
      <Collapse in={showValidation}>
        <Card withBorder mt="md">
          <Group spacing="xs" mb="xs">
            <NumberInput
              value={sampleCount}
              onChange={(val) => setSampleCount(val || 3)}
              min={1}
              max={3}
              size="xs"
              style={{ width: 80 }}
              disabled={isValidating}
              label="Samples"
            />
            <Button
              onClick={handleRunValidation}
              loading={isValidating}
              leftIcon={<IconRefresh size={14} />}
              size="xs"
              color="indigo"
              mt={20}
            >
              Run Verify
            </Button>
          </Group>

          {/* Validation Results */}
          {validationError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Validation Error"
              color="red"
              mb="xs"
            >
              {validationError}
            </Alert>
          )}

          {isValidating && (
            <Box mb="xs">
              <Group spacing="xs">
                <Loader size="sm" />
                <Text size="sm">Running pipeline on {sampleCount} samples...</Text>
              </Group>
            </Box>
          )}

          {validationResult && validationResult.samples.length === 0 && (
            <Alert icon={<IconAlertCircle size={16} />} title="No Samples" color="yellow" mb="xs">
              No sample data available for validation. Make sure you have imported some data from
              this integration.
            </Alert>
          )}

          {validationResult && validationResult.samples.length > 0 && (
            <Box mb="md">
              <Group position="apart" mb="xs">
                <Text size="sm" weight={500}>
                  Validation Results
                </Text>
                <Group spacing="xs">
                  <Badge color="green" variant="filled">
                    {validationResult.successCount} Success
                  </Badge>
                  <Badge color="red" variant="filled">
                    {validationResult.errorCount} Errors
                  </Badge>
                </Group>
              </Group>

              <Progress
                value={getSuccessRate()}
                color={
                  getSuccessRate() === 100 ? 'green' : getSuccessRate() > 50 ? 'yellow' : 'red'
                }
                size="sm"
                mb="xs"
              />

              <Text size="xs" color="dimmed" mb="xs">
                {validationResult.successCount} out of{' '}
                {validationResult.successCount + validationResult.errorCount} samples processed
                successfully
              </Text>

              <Box style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <Table fontSize="xs" style={{ width: '100%', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Input</th>
                      <th style={{ width: '30%' }}>Output</th>
                      <th style={{ width: '15%' }}>Status</th>
                      <th style={{ width: '15%' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.samples.map((sample, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            <Box>
                              <Button
                                variant="subtle"
                                size="xs"
                                compact
                                onClick={() => toggleSampleExpansion(index)}
                                leftIcon={
                                  expandedSamples.has(index) ? (
                                    <IconChevronDown size={12} />
                                  ) : (
                                    <IconChevronRight size={12} />
                                  )
                                }
                              >
                                Sample {index + 1}
                              </Button>
                            </Box>
                          </td>
                          <td>
                            <Code style={{ fontSize: 10, wordBreak: 'break-word' }}>
                              {sample.output !== null && sample.output !== undefined
                                ? typeof sample.output === 'object'
                                  ? JSON.stringify(sample.output)
                                  : String(sample.output)
                                : 'null'}
                            </Code>
                          </td>
                          <td>
                            {sample.error ? (
                              <Badge color="red" size="sm" leftSection={<IconX size={12} />}>
                                Error
                              </Badge>
                            ) : (
                              <Badge color="green" size="sm" leftSection={<IconCheck size={12} />}>
                                Success
                              </Badge>
                            )}
                          </td>
                          <td>
                            {sample.error && (
                              <Text size="xs" color="red" lineClamp={2}>
                                {sample.error}
                              </Text>
                            )}
                          </td>
                        </tr>
                        {expandedSamples.has(index) && (
                          <tr>
                            <td colSpan={4} style={{ padding: 0, maxWidth: 0, width: '100%' }}>
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  width: '100%',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <Text size="xs" weight={500} mb="xs" color="dimmed">
                                  Input Data:
                                </Text>
                                <ScrollArea
                                  style={{
                                    width: '100%',
                                  }}
                                >
                                  <Code
                                    block
                                    style={{
                                      fontSize: 10,
                                      whiteSpace: 'pre',
                                      display: 'block',
                                      overflow: 'auto',
                                      wordBreak: 'break-word',
                                      maxWidth: '100%',
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    {JSON.stringify(sample.input, null, 2)}
                                  </Code>
                                </ScrollArea>
                              </Box>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </Box>
            </Box>
          )}
        </Card>
      </Collapse>
    </Stack>
  );
};
